'use strict';

import get from 'lodash/get';
import { $http, globalSettings, sbJs } from '../app.config';
import { africaOptions } from '../globals';
import { I18n } from '../helpers/i18n';
import BaseModel from './Model';

/**
 * Represents functionality to create and validate request
 *
 * @property {number|undefined} destination Destination id
 * @property {number|undefined} tour Tour id
 * @property {string|undefined} date Flight date
 * @property {boolean|undefined} directFlight Direct flight
 * @property {number|undefined} airport Airport id
 * @property {number|undefined} combination Combination id
 * This param available only for USA instances.
 * @property {number|undefined} adults The number of adults
 * @property {number|undefined} children The number of children
 * @property {number|undefined} gender Gender code. See [[\africa\models\EnquireData::$gender]] for more details.
 * @property {string|undefined} name The name of customer
 * @property {string|undefined} surname The surname of customer
 * @property {string|undefined} email The email of customer
 * @property {string|undefined} phone The phone of customer
 * @property {string|undefined} comment The comment regarding trip
 * @property {string|undefined} newsletter Trigger for subscribe to newsletters.
 *
 */
export default class Request extends BaseModel {
    public destination: number;
    public tour: number;
    public date: string;
    public airport: number;
    public adults: number;
    public children: number;
    public gender: number;
    public name: string;
    public email: string;
    public phone: string;
    public comment: string;
    public newsletter: string;
    public marketingSource: string;
    public marketingSourceId: string;
    public otherDescription: string;

    constructor(data: { destination?: number, tour?: number, date?: string, airport?: number } = {}) {
        super(data);
        this.defineField('destination', {
            type: 'Number',
            validate: [
                {
                    validator: 'numberSize',
                    minOrEqual: 1, // mini hack, because all not int values cast to - 0
                    message: I18n.t('Please choose Destination'),
                },
            ],
        });
        this.defineField('tour', {
            type: 'Number',
            validate: [
                {
                    validator: 'numberSize',
                    minOrEqual: 1, // mini hack, because all not int values cast to - 0
                    message: I18n.t('Please choose Tour'),
                },
            ],
        });
        this.defineField('date', {
            type: 'String',
            validate: [
                {
                    validator: 'stringMatch',
                    regexp: /\d{4}-\d{2}-\d{2}/,
                    message: I18n.t('Please choose departure date'),
                },
            ],
        });
        this.defineField('directFlight', {
            type: 'Boolean',
            validate: [],
            defaultValue() {
                return false;
            },
        });
        this.defineField('directFlight', {
            type: 'Boolean',
            validate: [],
            defaultValue() {
                return false;
            },
        });
        this.defineField('airport', {
            type: 'Number',
            validate: [
                {
                    validator: 'numberSize',
                    minOrEqual: 1, // mini hack, because all not int values cast to - 0
                    message: I18n.t('Please choose airport'),
                },
            ],
        });
        this.defineField('combination', {
            type: 'Number',
            validate: [],
        });
        this.defineField('adults', {
            type: 'Number',
            validate: [
                {
                    validator: 'numberSize',
                    minOrEqual: 1,
                    message: I18n.t('Please fill in the mandatory fields'),
                },
            ],
        });
        this.defineField('children', {
            type: 'Number',
            validate: [
                {
                    validator: 'numberSize',
                    minOrEqual: 0,
                    message: I18n.t('Please fill in the mandatory fields'),
                },
            ],
        });
        this.defineField('gender', {
            type: 'Number',
            validate: [
                {
                    validator: 'numberSize',
                    minOrEqual: globalSettings.isDe ? 1 : 0,
                    message: I18n.t('Please choose your title'),
                },
            ],
        });
        this.defineField('name', {
            type: 'String',
            validate: [
                {
                    validator: 'presence',
                    message: I18n.t('Please fill in your first name'),
                },
            ],
        });
        this.defineField('surname', {
            type: 'String',
            validate: [
                {
                    validator: 'presence',
                    message: I18n.t('Please fill in your surname'),
                },
            ],
        });
        this.defineField('email', {
            type: 'String',
            validate: [
                {
                    validator: 'stringEmail',
                    allowDisplayName: false,
                    allowUtf8LocalPart: false,
                    message: I18n.t('E-mail is not valid'),
                },
            ],
        });
        this.defineField('phone', {
            type: 'String',
            validate: [],
        });
        this.defineField('comment', {
            type: 'String',
            validate: [],
        });
        this.defineField('newsletter', {
            type: 'Boolean',
            validate: [],
            defaultValue() {
                return false;
            },
        });
        this.defineField('marketingSource', {
            type: 'String',
            validate: [
                {
                    validator: 'presence',
                    message: I18n.t('Please answer the question'),
                    condition: () => {
                        return this.marketingSource !== 'off';
                    },
                },
            ],
        });
        this.defineField('marketingSourceId', {
            type: 'String',
            validate: [],
        });
        this.defineField('otherDescription', {
            type: 'String',
            validate: [
                {
                    validator: 'presence',
                    message: I18n.t('Please answer the question'),
                    condition: () => {
                        return this.marketingSource !== 'off' && this.marketingSourceId.toLowerCase() === 'other' && this.otherDescription === '';
                    },
                },
                {
                    validator: 'numberSize',
                    message: I18n.t('Field size exceeded'),
                    condition: () => {
                        return this.otherDescription.length > 1000;
                    },
                },
            ],
        });
        this.populate(data);
    }

    /**
     * Saves a new record
     */
    public save(captchaResponse?: any): Promise<any> {
        return new Promise((resolve, reject) => {
            const requestData = {
                ...this.serialize(),
                ...{
                    sourceBuster: sbJs.get,
                },
            };

            $http
                .post(africaOptions.api.routes.requests.create, {
                    request: requestData,
                    captcha: captchaResponse,
                })
                .then((response) => {
                    if (get(response, 'data.success', false)) {
                        resolve();
                        const emailHash = get(response, 'data.body.emailHash');
                        let successLink = '/request/success-' +
                            africaOptions.instance.locale +
                            '.htm?tid=' + get(response, 'data.body.tid') +
                            '&time=' + get(response, 'data.body.time');
                        if (emailHash) {
                            successLink += '&criteo=' + emailHash;
                        }
                        if (get(response, 'data.body.tour')) {
                            successLink += '&selectedTour=' + get(response, 'data.body.tour');
                        }
                        document.location.href = successLink;
                    } else if (get(response, 'data.errors', []).length > 0) {
                        reject(get(response, 'data.errors', []));
                    } else {
                        reject({
                            key: get(response, 'data.body.key'),
                            title: get(response, 'data.body.title', ''),
                        });
                    }
                }, () => {
                    reject();
                });
        });
    }
}



// WEBPACK FOOTER //
// ./src/models/Request.ts